import React, { useEffect, useMemo, useRef, useState } from 'react';
import { apiFetch } from '../api';
import TimelineItem from './TimelineItem';

const TimelineList = ({
  sortOrder = 'desc',
  selectedId,
  onSelect,
  filters = {},
  sessions: propSessions = null,  // Optional: pass sessions for controlled mode
  loading: propLoading = null     // Optional: pass loading state for controlled mode
}) => {
  // Internal state for uncontrolled mode (when sessions not passed as props)
  const [internalSessions, setInternalSessions] = useState([]);
  const [internalLoading, setInternalLoading] = useState(true);
  const [error, setError] = useState(null);
  const listRef = useRef(null);
  const [currentMonthKey, setCurrentMonthKey] = useState('');

  // Use prop values if provided (controlled mode), otherwise use internal state
  const sessions = propSessions !== null ? propSessions : internalSessions;
  const loading = propLoading !== null ? propLoading : internalLoading;
  const isControlled = propSessions !== null;

  // Only fetch if not in controlled mode
  useEffect(() => {
    if (isControlled) return; // Skip fetching in controlled mode

    let mounted = true;
    const load = async () => {
      try {
        setInternalLoading(true);
        // Build query string with filters
        const params = new URLSearchParams({
          filter: 'all',
          sort: sortOrder
        });
        if (filters.student_id) params.set('student_id', filters.student_id);
        if (filters.subject) params.set('subject', filters.subject);
        if (filters.date_range) params.set('date_range', filters.date_range);

        const res = await apiFetch(`/api/sessions/list?${params.toString()}`);
        if (!res.ok) throw new Error(`Failed to load sessions: ${res.status}`);
        const data = await res.json();
        if (mounted) {
          setInternalSessions(data.sessions || []);
          setError(null);
        }
      } catch (e) {
        if (mounted) setError(e.message);
      } finally {
        if (mounted) setInternalLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [isControlled, sortOrder, filters.student_id, filters.subject, filters.date_range]);

  const onKeyDown = (e) => {
    if (!sessions.length) return;
    const idx = sessions.findIndex(s => s.id === selectedId);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = sessions[Math.min(idx + 1, sessions.length - 1)] || sessions[0];
      onSelect && onSelect(next.id);
      scrollItemIntoView(next.id);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = sessions[Math.max(idx - 1, 0)] || sessions[0];
      onSelect && onSelect(prev.id);
      scrollItemIntoView(prev.id);
    } else if (e.key === 'Enter' && selectedId) {
      // already handled by selection; keep for a11y
    }
  };

  const scrollItemIntoView = (id) => {
    const el = listRef.current?.querySelector(`[data-session-id="${CSS.escape(id)}"]`);
    el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  };

  // Utilities for month grouping/labels
  const getMonthKey = (session) => {
    // Prefer month from ID pattern audioMMDD
    const idMatch = session?.id?.match(/audio(\d{2})(\d{2})/);
    if (idMatch) {
      const mm = idMatch[1];
      // Use year from processed date if available
      if (session?.date) {
        const d = new Date(session.date);
        if (!isNaN(d)) {
          return `${d.getFullYear()}-${mm}`;
        }
      }
      // Fallback to month-only key
      return mm; // e.g., "07"
    }
    // Fallback to ISO date from backend
    const sessionDate = session?.date || session?.started_at || session?.created_at;
    if (sessionDate) {
      const d = new Date(sessionDate);
      if (!isNaN(d)) {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        return `${y}-${m}`;
      }
    }
    return 'Unknown';
  };

  const getMonthLabel = (key) => {
    if (!key || key === 'Unknown') return 'Unknown';
    // Month-only key (e.g., "07")
    if (/^\d{2}$/.test(key)) {
      const idx = Math.max(0, Math.min(11, parseInt(key, 10) - 1));
      return new Date(2000, idx, 1).toLocaleString(undefined, { month: 'long' });
    }
    const [y, m] = key.split('-');
    const idx = Math.max(0, Math.min(11, (parseInt(m, 10) || 1) - 1));
    const monthName = new Date(2000, idx, 1).toLocaleString(undefined, { month: 'long' });
    return `${monthName} ${y}`;
  };

  const groups = useMemo(() => {
    const byMonth = new Map();
    for (const s of sessions) {
      const key = getMonthKey(s);
      if (!byMonth.has(key)) byMonth.set(key, []);
      byMonth.get(key).push(s);
    }
    // Preserve the incoming order (already sorted desc) by iterating sessions again for keys order
    const orderedKeys = [];
    for (const s of sessions) {
      const k = getMonthKey(s);
      if (!orderedKeys.includes(k)) orderedKeys.push(k);
    }
    return orderedKeys.map((k) => ({ key: k, label: getMonthLabel(k), items: byMonth.get(k) || [] }));
  }, [sessions]);

  // Scrollspy for month headers
  useEffect(() => {
    if (!listRef.current) return;
    const headers = listRef.current.querySelectorAll('[data-month-key]');
    if (!headers.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the topmost intersecting month header
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        const top = visible[0] || entries.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (top?.target?.dataset?.monthKey) {
          setCurrentMonthKey(top.target.dataset.monthKey);
        }
      },
      { root: listRef.current, threshold: [0, 1], rootMargin: '0px 0px -70% 0px' }
    );
    headers.forEach((h) => observer.observe(h));
    return () => observer.disconnect();
  }, [groups.length]);

  const handleJumpToMonth = (key) => {
    if (!key || !listRef.current) return;
    const el = listRef.current.querySelector(`#month-${CSS.escape(key)}`);
    el?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="timeline loading">
        <div className="timeline-rail" />
        {[...Array(6)].map((_, i) => (
          <div key={i} className="timeline-item skeleton" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="timeline error">
        <div className="empty-card">
          <div className="icon">❌</div>
          <div className="title">Failed to load sessions</div>
          <div className="subtitle">{error}</div>
        </div>
      </div>
    );
  }

  if (!sessions.length) {
    return (
      <div className="timeline empty">
        <div className="empty-card">
          <div className="icon">🗂️</div>
          <div className="title">No sessions</div>
          <div className="subtitle">New sessions will appear here</div>
        </div>
      </div>
    );
  }

  return (
    <div className="timeline-wrapper">
      <div className="timeline-controls">
        <div className="current-pill" aria-live="polite">
          {getMonthLabel(currentMonthKey) || '—'}
        </div>
        {!!groups.length && (
          <label className="month-jump">
            <span className="sr-only">Jump to month</span>
            <select value={currentMonthKey} onChange={(e) => handleJumpToMonth(e.target.value)} aria-label="Jump to month">
              {groups.map((g) => (
                <option key={g.key} value={g.key}>{g.label}</option>
              ))}
            </select>
          </label>
        )}
      </div>
      <div
        className="timeline"
        role="listbox"
        aria-label="Learning sessions timeline"
        tabIndex={0}
        onKeyDown={onKeyDown}
        ref={listRef}
      >
        <div className="timeline-rail" />
        {groups.map((group) => (
          <div key={group.key} className="timeline-group">
            <div
              id={`month-${group.key}`}
              className={`timeline-month${currentMonthKey === group.key ? ' active' : ''}`}
              data-month-key={group.key}
              role="heading"
              aria-level={2}
            >
              {group.label}
            </div>
            {group.items.map((s) => (
              <TimelineItem
                key={s.id}
                id={s.id}
                preview={s.preview}
                sourceType={s.source_type}
                sessionDate={s.date || s.started_at || s.created_at}
                hasTranscript={s.has_transcript}
                selected={selectedId === s.id}
                onClick={() => onSelect && onSelect(s.id)}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default TimelineList;
